import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SEO from '../../components/SEO';
import { SITE_URL } from '../../config/appConfig';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import CompactHeroSearch from '../../components/CompactHeroSearch';
import ProviderCardSkeleton from '../../components/ProviderCardSkeleton';
import {
  FeaturesSection,
  HowItWorksSection,
  BenefitsSection,
  CTASection,
  TopRatedProvidersSection,
  ProviderSection,
  FeaturedProvidersSection,
} from './components';
import {
  SearchFilters,
  Provider,
} from '../../types/api.types';
import {
  // serviceService,
  providerService,
} from '../../services/booking.service';
import { getFeaturedProviders as fetchFeaturedProvidersAPI, trackFeaturedBooking } from '../../services/featured.service';
import { useGeolocation } from '../../context/GeolocationContext';

const BookingModal = lazy(() => import('../../components/BookingModal'));

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get geolocation from context (persisted across navigations)
  const { location: userLocation } = useGeolocation();

  // State management
  const [featuredProviders, setFeaturedProviders] = useState<Provider[]>([]);
  const [topRatedProviders, setTopRatedProviders] = useState<Provider[]>([]);
  const [hasPaidFeatured, setHasPaidFeatured] = useState(false);
  const [featuredCity, setFeaturedCity] = useState<string | null>(null);
  const [topRatedCity, setTopRatedCity] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [topRatedLoading, setTopRatedLoading] = useState(false);

  // Search state
  const [isSearchApplied, setIsSearchApplied] = useState<boolean>(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Provider booking modal state
  const [isProviderBookingModalOpen, setIsProviderBookingModalOpen] = useState(false);
  const [selectedProviderForBooking, setSelectedProviderForBooking] = useState<Provider | undefined>();

  // Fetch paid featured providers only
  const fetchFeaturedProviders = useCallback(async () => {
    try {
      setFeaturedLoading(true);
      
      // Fetch paid featured providers only
      const featuredResponse = await fetchFeaturedProvidersAPI({
        limit: 3,
      });
      
      if (featuredResponse.data && featuredResponse.data.length > 0) {
        setFeaturedProviders(featuredResponse.data as Provider[]);
        setHasPaidFeatured(true);
        // Set city from first provider's location if available
        const firstLocation = (featuredResponse.data[0] as any)?.address?.city;
        if (firstLocation) {
          setFeaturedCity(firstLocation);
        }
      } else {
        // No paid featured providers - clear the section
        setFeaturedProviders([]);
        setHasPaidFeatured(false);
        setFeaturedCity(null);
      }
    } catch (error) {
      console.log("Failed to fetch featured providers:", error);
      setFeaturedProviders([]);
      setHasPaidFeatured(false);
    } finally {
      setFeaturedLoading(false);
    }
  }, []);

  // Fetch top-rated providers by city (separate from paid featured)
  const fetchTopRatedProviders = useCallback(async () => {
    if (!userLocation) return;
    
    try {
      setTopRatedLoading(true);
      
      const response = await providerService.getTopRatedByCity({
        latitude: userLocation.latitude,
        longitude: userLocation.longitude,
        limit: 3,
      });
      
      if (response.data && response.data.length > 0) {
        setTopRatedProviders(response.data);
        setTopRatedCity(response.city);
      }
    } catch (error) {
      console.log("Failed to fetch top-rated providers:", error);
    } finally {
      setTopRatedLoading(false);
    }
  }, [userLocation]);

  const fetchProvider = useCallback(
    async () => {
      try {
        setLoading(true);
        const params: any = { isActive: true };
        
        // Add user location if available for distance calculation
        if (userLocation) {
          params.latitude = userLocation.latitude;
          params.longitude = userLocation.longitude;
          params.sortByDistance = true;
        }
        
        const providersRes = await providerService.getAll(params);
        setProviders(providersRes.data);
        
        // Don't set fallback featured providers - only show paid ones
      } catch (error) {
        console.log("Failed to fetch providers:", error)
      } finally {
        setLoading(false);
      }
    },
    [userLocation],
  )

  // Fetch paid featured providers on mount
  useEffect(() => {
    fetchFeaturedProviders();
  }, [fetchFeaturedProviders]);

  // Fetch top-rated providers when location becomes available
  useEffect(() => {
    fetchTopRatedProviders();
  }, [fetchTopRatedProviders]);

  // Trigger search for Healthcare category on initial load
  useEffect(() => {
    if (!isSearchApplied) {
      fetchProvider();
    }
  }, [fetchProvider, isSearchApplied]);

  const handleSearch = React.useCallback(async (filters: SearchFilters) => {
    if (filters.query || filters.category || filters.location) {
      setIsSearchApplied(true);
    } else {
      setIsSearchApplied(false);
      return;
    }
    setLoading(true);
    setSearchError(null);

    try {
      let providersData: Provider[] = [];

      if (filters.query) {
        // Text search: name/specialty/service + optional category + city — all server-side
        const res = await providerService.search(filters.query, {
          category: filters.category || undefined,
          city: filters.location?.trim() || undefined,
        });
        providersData = res.data ?? [];
      } else {
        // Category-only or city-only browse — pass as server params
        const params: {
          isActive: boolean;
          category?: string;
          city?: string;
          latitude?: number;
          longitude?: number;
          sortByDistance?: boolean;
        } = { isActive: true };

        if (filters.category) params.category = filters.category;
        if (filters.location?.trim()) params.city = filters.location.trim();

        if (userLocation) {
          params.latitude = userLocation.latitude;
          params.longitude = userLocation.longitude;
          params.sortByDistance = true;
        }

        const res = await providerService.getAll(params);
        providersData = res.data ?? [];
      }

      setProviders(providersData);

      // Scroll to results
      setTimeout(() => {
        const resultsSection = document.getElementById('search-results');
        if (resultsSection) {
          resultsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchError('Failed to fetch search results. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userLocation]);

  // Check URL params on mount to auto-open modal
  useEffect(() => {
    const bookingParam = searchParams.get('booking');
    if (bookingParam === 'true') {
      setIsProviderBookingModalOpen(true);
    }
  }, [searchParams]);

  const handleClearSearch = React.useCallback(() => {
    setIsSearchApplied(false);
    setSearchError(null);
  }, []);

  const handleProviderClick = React.useCallback((provider: Provider) => {
    // Navigate to the new Provider Details Page using username or fallback to _id
    const identifier = typeof provider === 'string'
      ? provider
      : (provider.username || provider._id);
    navigate(`/provider/${identifier}`);
  }, [navigate]);

  const handleProviderBooking = React.useCallback((provider: Provider) => {
    setSelectedProviderForBooking(provider);
    setIsProviderBookingModalOpen(true);

    // Track booking for featured providers
    if ((provider as any).featuredSettings?.isFeatured) {
      trackFeaturedBooking(provider._id);
    }

    // Set URL params
    const newParams = new URLSearchParams(searchParams);
    newParams.set('booking', 'true');
    newParams.set('providerId', provider._id);
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);



  const handleCTAClick = React.useCallback(() => {
    navigate('/providers');
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Book Appointments & Join Queues Instantly"
        description="Appointigo – Find top-rated service providers near you. Book appointments, join queues, and manage bookings seamlessly. Healthcare, beauty, fitness, and more."
        canonicalPath="/"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'Appointigo',
          url: SITE_URL,
          description: 'Find and book appointments with top service providers near you.',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'INR' },
        }}
      />
      <Header />

      <main>
      {/* Compact Hero Section with Search */}
      <CompactHeroSearch onSearch={handleSearch} isSearchApplied={isSearchApplied} />

      {/* Search Results Section */}
      {loading ? (
        <section id="search-providers" className="py-8 sm:py-16 px-3 xs:px-4 sm:px-6 lg:px-8 bg-white border-b">
          <div className="max-w-7xl mx-auto">
            {/* Title & View All skeleton */}
            <div className="flex items-center justify-between mb-4 sm:mb-6 animate-pulse">
              <div className="h-6 sm:h-8 bg-gray-200 rounded w-32 sm:w-40" />
              <div className="h-7 sm:h-8 bg-gray-200 rounded-lg w-20 sm:w-24" />
            </div>
            <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 xs:gap-4 sm:gap-6">
              <ProviderCardSkeleton count={6} />
            </div>
          </div>
        </section>
      ) : (
        <ProviderSection
          providers={providers}
          isSearchApplied={isSearchApplied}
          onProviderClick={handleProviderClick}
          onProviderBooking={handleProviderBooking}
          onClearSearch={handleClearSearch}
        />
      )}

      {/* Search Error */}
      {searchError && (
        <section className="px-3 xs:px-4 sm:px-6 lg:px-8 bg-red-50">
          <div className="max-w-7xl mx-auto py-3">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
              <strong className="font-bold">Error!</strong>
              <span className="block sm:inline"> {searchError}</span>
            </div>
          </div>
        </section>
      )}

      {/* Featured Healthcare Providers - Only show if there are paid featured providers */}
      {(hasPaidFeatured || featuredLoading) && (
        <FeaturedProvidersSection
          providers={featuredProviders}
          loading={loading || featuredLoading}
          onProviderClick={handleProviderClick}
          onProviderBooking={handleProviderBooking}
          city={featuredCity}
        />
      )}

      {/* Top Rated Providers Section - only show when loading or has results */}
      {(topRatedLoading || topRatedProviders.length > 0) && (
        <TopRatedProvidersSection
          providers={topRatedProviders}
          loading={loading || topRatedLoading}
          onProviderClick={handleProviderClick}
          onProviderBooking={handleProviderBooking}
          city={topRatedCity}
        />
      )}

      {/* Features Section */}
      <FeaturesSection />

      {/* All Services Section - Hidden but component preserved */}
      {/* <div id="all-services">
        <FeaturedServicesSection
          services={featuredServices}
          loading={loading}
          onServiceClick={handleServiceClick}
        />
      </div> */}

      {/* How It Works Section */}
      <HowItWorksSection />

      {/* Benefits Section */}
      <BenefitsSection />

      {/* CTA Section */}
      <CTASection onCTAClick={handleCTAClick} />

      </main>

      {/* Footer */}
      <Footer />

      {/* Provider Booking Modal */}
      {isProviderBookingModalOpen && (
        <Suspense fallback={null}>
          <BookingModal
            provider={selectedProviderForBooking}
            isOpen={isProviderBookingModalOpen}
            onClose={() => setIsProviderBookingModalOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
};

export default Homepage;
